import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useSupabase } from '../contexts/SupabaseContext';

export interface Document {
  id: string;
  order_id: string;
  seller_user_id: string;
  buyer_user_id: string;
  document_type: string;
  document_url: string;
  is_visible_to_buyer: boolean;
  is_saved_by_seller: boolean;
  created_at: string;
  updated_at: string;
  order?: {
    order_number: string;
  };
}

export const useDocuments = () => {
  const { user } = useSupabase();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch documents based on user role
  const fetchDocuments = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get user account type
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('account_type')
        .eq('id', user.id)
        .single();
      
      if (userError) throw userError;
      
      // Fetch documents without joining orders to avoid RLS recursion
      let query = supabase
        .from('documents')
        .select('*');
      
      // Filter based on user role
      if (userData.account_type === 'supplier') {
        query = query.eq('seller_user_id', user.id);
      } else if (userData.account_type === 'buyer') {
        query = query.eq('buyer_user_id', user.id).eq('is_visible_to_buyer', true);
      } else if (userData.account_type === 'admin') {
        // Admin can see all documents
      } else {
        throw new Error('Unknown account type');
      }
      
      // Order by most recent first
      query = query.order('created_at', { ascending: false });
      
      const { data: documentsData, error: documentsError } = await query;
      
      if (documentsError) throw documentsError;
      
      // Fetch order numbers separately to avoid RLS issues
      const documentsWithOrders = await Promise.all(
        (documentsData || []).map(async (doc) => {
          try {
            const { data: orderData } = await supabase
              .from('orders')
              .select('order_number')
              .eq('id', doc.order_id)
              .single();
            
            return {
              ...doc,
              order: orderData ? { order_number: orderData.order_number } : undefined
            };
          } catch (err) {
            // If we can't fetch the order, just return the document without order info
            console.warn('Could not fetch order for document:', doc.id, err);
            return doc;
          }
        })
      );
      
      setDocuments(documentsWithOrders);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Create a new document
  const createDocument = useCallback(async (
    orderId: string,
    buyerUserId: string,
    documentType: string,
    documentUrl: string
  ) => {
    if (!user) return null;
    
    try {
      const { data, error } = await supabase
        .from('documents')
        .insert({
          order_id: orderId,
          seller_user_id: user.id,
          buyer_user_id: buyerUserId,
          document_type: documentType,
          document_url: documentUrl,
          is_visible_to_buyer: false,
          is_saved_by_seller: false
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Refresh documents list
      fetchDocuments();
      
      return data;
    } catch (err) {
      console.error('Error creating document:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      return null;
    }
  }, [user, fetchDocuments]);

  // Update document visibility to buyer
  const updateVisibilityToBuyer = useCallback(async (documentId: string, isVisible: boolean) => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('documents')
        .update({ is_visible_to_buyer: isVisible })
        .eq('id', documentId)
        .eq('seller_user_id', user.id);
      
      if (error) throw error;
      
      // Refresh documents list
      fetchDocuments();
      
      return true;
    } catch (err) {
      console.error('Error updating document visibility:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      return false;
    }
  }, [user, fetchDocuments]);

  // Update document saved status
  const updateSavedStatus = useCallback(async (documentId: string, isSaved: boolean) => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('documents')
        .update({ is_saved_by_seller: isSaved })
        .eq('id', documentId)
        .eq('seller_user_id', user.id);
      
      if (error) throw error;
      
      // Refresh documents list
      fetchDocuments();
      
      return true;
    } catch (err) {
      console.error('Error updating document saved status:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      return false;
    }
  }, [user, fetchDocuments]);

  // Delete a document
  const deleteDocument = useCallback(async (documentId: string) => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId)
        .eq('seller_user_id', user.id);
      
      if (error) throw error;
      
      // Refresh documents list
      fetchDocuments();
      
      return true;
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      return false;
    }
  }, [user, fetchDocuments]);

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchDocuments();
    }
  }, [user, fetchDocuments]);

  return {
    documents,
    loading,
    error,
    fetchDocuments,
    createDocument,
    updateVisibilityToBuyer,
    updateSavedStatus,
    deleteDocument
  };
};